import React, { FC, useMemo } from 'react';

import { IconFallbackImage } from 'components/icons/fallback-image';
import { useNutritionModifierFacts } from 'components/nutrition/use-nutrition-modifier-facts';
import { ProductListItem } from 'components/product-list-item';
import { MenuObjectTypes } from 'enums/menu';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { StyledControls } from './modifier.styled';
import { IModifierListItemProps } from './types';
import { getModifierImage, getModifierOptionImage } from './utils';

export const ModifierListItem: FC<IModifierListItemProps> = ({
  controls,
  content,
  isListItemClickable = false,
  onListItemClick,
  selectedOption,
}) => {
  const { formatCalories } = useMenuCalories();
  const enableGermanLegalRequirement =
    useFlag(LaunchDarklyFlag.ENABLE_GERMAN_LEGAL_REQUIREMENT) || false;
  const image = useMemo(
    () =>
      content._type === MenuObjectTypes.ITEM_OPTION
        ? getModifierImage(content)
        : getModifierOptionImage(content),
    [content]
  );

  const selectedModifer = useMemo(() => {
    if (content._type === MenuObjectTypes.ITEM_OPTION_MODIFIER) {
      return content;
    } else {
      return selectedOption;
    }
  }, [content, selectedOption]);

  const nutritionModifierFacts = useNutritionModifierFacts({
    selectedModifer,
    useNutritionAllergenFromModifierMultiplier: enableGermanLegalRequirement,
  });

  const calories = useMemo(
    () =>
      content._type === MenuObjectTypes.ITEM_OPTION
        ? formatCalories(selectedOption?.nutrition?.calories)
        : formatCalories(content.nutrition?.calories),
    [content, formatCalories, selectedOption]
  );

  return (
    <ProductListItem
      isClickable={isListItemClickable}
      onProductClick={onListItemClick}
      productProps={{
        hasModifierNutrition: enableGermanLegalRequirement,
        nutrition: nutritionModifierFacts || [],
        name: content.name?.locale,
        image,
        fallbackImage: IconFallbackImage,
        imageSize: 'small',
        calories,
      }}
      controls={
        <StyledControls $hasListItemClickListener={isListItemClickable}>{controls}</StyledControls>
      }
    />
  );
};
